import { useEffect, useRef } from "react";

const useChangeTabForTimeout = (
  timestamp,
  timeoutSeconds = 7200, // Total timeout duration (e.g., 120 minutes)
  warnSecondsBefore = 300, // Seconds before timeout to warn
  active = false,
  pathToFavicon = "/timeout_warning.ico",
  newTitle = "TIMING OUT",
  getCurrentTime = () => Math.floor(Date.now() / 1000), // Dependency injection for testability
) => {
  const originalTitle = useRef(document.title);
  const originalFaviconRef = useRef(
    document.getElementById("favicon")?.href || "",
  );
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Copy ref values to local variables for use in cleanup
    const savedOriginalTitle = originalTitle.current;
    const savedOriginalFavicon = originalFaviconRef.current;

    const favicon = document.getElementById("favicon");

    if (!active || !timestamp) {
      document.title = savedOriginalTitle;
      if (favicon) favicon.href = savedOriginalFavicon;
      return;
    }

    // Check if Notification API exists before using it.
    if ("Notification" in window) {
      if (
        Notification.permission !== "granted" &&
        Notification.permission !== "denied"
      ) {
        Notification.requestPermission();
      }
    }

    const warnAfter = +timeoutSeconds - warnSecondsBefore;

    const showNotification = () => {
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("Session Timeout Warning", {
          body: "Your session is about to expire.",
          icon: pathToFavicon,
        });
      }
    };

    const updateTitleAndFavicon = () => {
      const currentTime = getCurrentTime();
      if (currentTime - timestamp >= warnAfter) {
        document.title = newTitle;
        if (favicon) favicon.href = pathToFavicon;
        showNotification();
      } else {
        document.title = savedOriginalTitle;
        if (favicon) favicon.href = savedOriginalFavicon;
      }
    };

    // Clear any existing timeout before setting a new one
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset Title & Favicon immediately when timestamp changes
    document.title = savedOriginalTitle;
    if (favicon) favicon.href = savedOriginalFavicon;

    // Calculate remaining time until warning should appear
    const timeRemaining = warnAfter - (getCurrentTime() - timestamp);

    if (timeRemaining <= 0) {
      updateTitleAndFavicon(); // If past threshold, apply warning immediately
    } else {
      timeoutRef.current = setTimeout(
        updateTitleAndFavicon,
        timeRemaining * 1000,
      );
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      // Use saved values in cleanup
      document.title = savedOriginalTitle;
      if (favicon) favicon.href = savedOriginalFavicon;
    };
  }, [
    timestamp,
    timeoutSeconds,
    warnSecondsBefore,
    active,
    pathToFavicon,
    newTitle,
    getCurrentTime,
  ]);
};

export default useChangeTabForTimeout;
